// Laser packaging kiosk (/laser/current-packaging): the rules the unload screens share.
// Pure: no React, no fetch. Tests: kioskRules.test.js.

// The "All parts" choice of the part switcher (a part_id is never a string).
export const ALL_PARTS = "all";

// The part switcher's starting choice for a payload: every part together when the
// sheet makes more than one, else the only part.
export function defaultPartSelection(parts) {
  const list = parts || [];
  if (list.length > 1) return ALL_PARTS;
  return list[0]?.part_id ?? null;
}

// Pallet states nothing may be added to: the pallet left (Shipped), is waiting for the
// customer with its paperwork done (Staged), or was merged into another (Consolidated).
const DO_NOT_ADD = {
  shipped: "PALLET ALREADY SHIPPED",
  staged: "PALLET STAGED FOR PICKUP",
  consolidated: "PALLET MERGED INTO ANOTHER",
};

// A box riding on another order's pallet physically sits on that host pallet.
export function ridesOnHost(dest) {
  return !!(dest && (dest.riding_to_pallet_short_id || dest.riding_to_pallet_status));
}

// The pallet the operator walks to: the host pallet for a ride-along box, else its own.
export function physicalPalletName(dest) {
  if (!dest) return "—";
  if (ridesOnHost(dest)) return dest.riding_to_pallet_short_id || "host pallet";
  if (dest.pallet_short_id) return dest.pallet_short_id;
  return dest.pallet_num != null ? `P${dest.pallet_num}` : "—";
}

// Why nothing may go on this destination's pallet, or null when it may. A ride-along box is
// judged by its host pallet only (its home pallet can read Consolidated once emptied).
export function doNotAddReason(dest) {
  if (!dest || dest.kind !== "packaging") return null;
  const status = ridesOnHost(dest) ? dest.riding_to_pallet_status : dest.pallet_status;
  return DO_NOT_ADD[String(status || "").toLowerCase()] || null;
}

// The order to fill a part's destinations in, numbered "1 of N": its pallets as the API
// lists them (this order's first, then other orders', by box number), then pieces an order
// still needs, then surplus. A destination nothing may be added to gets no number.
// Returns a Map dest -> {seq, of}.
export function fillOrder(destinations) {
  const list = destinations || [];
  const rank = (d) => (d.kind === "packaging" ? 0 : d.kind === "owed" ? 1 : 2);
  const fillable = list
    .map((d, i) => ({ d, i }))
    .filter(({ d }) => !doNotAddReason(d))
    .sort((a, b) => rank(a.d) - rank(b.d) || a.i - b.i);
  const out = new Map();
  fillable.forEach(({ d }, k) => out.set(d, { seq: k + 1, of: fillable.length }));
  return out;
}

// A holding pallet (jobq-api holding pallets, 2026-10-27): a laser task's WIP pallet, which every part goes on at the
// laser. Kiosk destinations carry `wip_task_id` and `wip_station` (the live next station, "Packing" when no in-house
// step is left); an older API sends neither, and the card reads as before.
export function isHoldingDestination(dest) {
  return dest?.kind === "packaging" && dest?.wip_task_id != null;
}

// "HOLDING PALLET · SO-1147 · then BRAKE".
export function holdingDestinationLabel(dest) {
  if (!isHoldingDestination(dest)) return "";
  const station = dest.wip_station || dest.wip_next_station || "Packing";
  const bits = ["HOLDING PALLET"];
  if (dest.dest_so_number) bits.push(dest.dest_so_number);
  bits.push(`then ${String(station).toUpperCase()}`);
  return bits.join(" · ");
}

// The fill-order numbers the cards and tiles show: fillOrder's, except none when the part's ONLY destination is a
// holding pallet ("1 of 1" says nothing: every piece goes on it). The sheet map keeps fillOrder's numbers.
export function shownFillOrder(destinations) {
  const list = destinations || [];
  if (list.length === 1 && isHoldingDestination(list[0])) return new Map();
  return fillOrder(list);
}

// Destinations in the order to fill (DO NOT ADD ones last, in API order).
export function destinationsInFillOrder(destinations) {
  const order = fillOrder(destinations);
  const list = destinations || [];
  return [...list].sort((a, b) => {
    const sa = order.get(a)?.seq ?? Infinity;
    const sb = order.get(b)?.seq ?? Infinity;
    return sa - sb || list.indexOf(a) - list.indexOf(b);
  });
}

// Pieces per finished assembly when this part is the primary of one being shipped, else 1.
export function piecesPerAssembly(part) {
  if (!part?.assembly_id || !part?.assembly_name) return 1;
  return Math.max(1, parseInt(part.primary_part_quantity, 10) || 1);
}

// "100 assemblies" for a piece count of an assembly primary (whole assemblies only), or
// null when the part isn't one. The kiosk counts PIECES everywhere (what comes off the
// sheet); this is the side note.
export function assemblyNote(pcs, part) {
  const ppq = piecesPerAssembly(part);
  if (!part?.assembly_id || !part?.assembly_name) return null;
  const n = Math.floor((Number(pcs) || 0) / ppq);
  return `= ${n} ${n === 1 ? "assembly" : "assemblies"}${ppq > 1 ? ` (${ppq} pcs each)` : ""}`;
}

// Piece totals of a part's destinations by kind.
export function pieceTotals(part) {
  const t = { packaging: 0, owed: 0, surplus: 0, blocked: 0 };
  for (const d of part?.destinations || []) {
    const q = Number(d.qty) || 0;
    if (d.kind === "packaging") {
      if (doNotAddReason(d)) t.blocked += q;
      else t.packaging += q;
    } else if (d.kind === "owed") t.owed += q;
    else if (d.kind === "surplus") t.surplus += q;
  }
  return t;
}

// The line under a pallet's name naming its beacon. null when the API sent no beacon
// fields (an older API): the card then says nothing about beacons.
export function beaconLine(dest) {
  if (!dest || !("pallet_beacon_id" in dest)) return null;
  // The API names the beacon of the pallet the box sits on: the host for a ride-along box.
  const host = ridesOnHost(dest) ? ` (on ${physicalPalletName(dest)})` : "";
  if (!dest.pallet_beacon_id) return { text: `No beacon on this pallet${host}`, tone: "none" };
  return { text: `${dest.pallet_beacon_id}${host}`, tone: dest.pallet_beacon_lit ? "lit" : "on" };
}
